import React, { useMemo, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { GridReadyEvent, GetRowIdParams } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import CustomAgGrid from '../../../../../shared/components/custom/Grid/CustomAgGrid'
import { CustomReportTableSection } from '../../../../../redux/context/customReports/typesSection'
import { createExcelStyles } from '../../../../../shared/utils/excelStyles'
import { categoryRowCellMetadataProvider } from './utils/cellMetadataProviders'
import { getStatusBarConfig } from '../../../../../shared/utils/agGridConfig'
import { useGridRegistration } from './useGridRegistration'
import { filtersSelector } from '../../../../../redux/context/filters/selectors'
import './style.css'
import { getTitleByLanguage } from '../customReportTable/utils'
import { useRowFilteringCategories } from '../../hooks/useRowFilteringCategories'
import { buildTransposedRowData, TransposedRowData } from './utils/transposedRowData'
import { buildTransposedColumnDefs } from './utils/transposedColumnDefs'

interface CustomReportProps {
  section: CustomReportTableSection
}

const CustomReportAgGridTransposed: React.FC<CustomReportProps> = ({
  section: { variables, categories, name, nameEn, nameFi, nameSv, id: sectionId }
}) => {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact<TransposedRowData>>(null)
  const { displayEmptyRows } = useSelector(filtersSelector)
  const { onGridReady: exportOnGridReady } = useGridRegistration(
    sectionId,
    getTitleByLanguage({ name, nameEn, nameFi, nameSv }) || 'Custom Report',
    undefined,
    categoryRowCellMetadataProvider
  )

  const excelStyles = useMemo(() => createExcelStyles(), [])
  const getRowId = useCallback((params: GetRowIdParams) => params.data?.categoryId || '', [])

  const { isExternalFilterPresent, doesExternalFilterPass } = useRowFilteringCategories(displayEmptyRows, variables)

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      exportOnGridReady(params)
    },
    [exportOnGridReady]
  )

  const { rowData, columnDefs } = useMemo(() => {
    const rows = buildTransposedRowData(categories || [])
    const cols = buildTransposedColumnDefs({
      variables,
      rows,
      companyLabel: t('customReportCategoryType:company'),
      dimensionLabel: t('customReportCategoryType:dimension'),
      leafLabel: t('customReportCategoryType:leaf')
    })
    return { rowData: rows, columnDefs: cols }
  }, [variables, categories, t])

  return (
    <CustomAgGrid
      className="custom-report-grid"
      ref={gridRef}
      rowData={rowData}
      columnDefs={columnDefs}
      suppressContextMenu
      cellSelection
      excelStyles={excelStyles}
      onGridReady={onGridReady}
      getRowId={getRowId}
      isExternalFilterPresent={isExternalFilterPresent}
      doesExternalFilterPass={doesExternalFilterPass}
      statusBar={getStatusBarConfig()}
      defaultColDef={{
        suppressHeaderMenuButton: true,
        sortable: true,
        filter: false,
        resizable: true
      }}
    />
  )
}

export default CustomReportAgGridTransposed
